import type { RefObject } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ChatMessage } from "~/entities/chat";
import { InChatSearchResults } from "~/features/in-chat-search";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";
import { Input } from "~/shared/ui/input";

import { ICON_BASE } from "./chat-header-tokens";

interface ChatHeaderSearchBarProps {
    value: string;
    onChange: (value: string) => void;
    onClose: () => void;
    chatId?: string | null;
    messages: ChatMessage[];
    isFetching: boolean;
    onSelect: (messageId: string) => void;
    containerRef: RefObject<HTMLDivElement | null>;
}

// Search-mode строка хедера: поле ввода (autoFocus, Esc → close) + кнопка
// закрытия + выпадающий список результатов.
export function ChatHeaderSearchBar({
    value,
    onChange,
    onClose,
    chatId,
    messages,
    isFetching,
    onSelect,
    containerRef,
}: ChatHeaderSearchBarProps) {
    const { t } = useTranslation("chat");

    return (
        <div ref={containerRef} className="relative flex min-w-0 flex-1 items-center gap-2">
            <Search className={cn(ICON_BASE, "shrink-0 text-zinc-500")} />
            <Input
                autoFocus
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t("header.search.placeholder")}
                className="h-9 flex-1 border-none bg-transparent text-sm placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                onKeyDown={(event) => {
                    if (event.key === "Escape") onClose();
                }}
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="size-9 text-zinc-500 hover:text-zinc-300"
                aria-label={t("header.search.close")}
            >
                <X className={ICON_BASE} />
            </Button>
            {chatId && (
                <InChatSearchResults
                    query={value}
                    messages={messages}
                    isFetching={isFetching}
                    onSelect={onSelect}
                />
            )}
        </div>
    );
}
